import { AlertTriangle, CheckCircle2, ChevronDown, CircleHelp, Eye, Images, Plus, RotateCcw, Search, XCircle } from 'lucide-react'
import { useState } from 'react'

import { Button, ProductThumb } from '@krp/ui-kit'

import '../../../shared/platform.css'
import { thumbOf, type OpenCommonProduct, type ProductPhotoUrl } from '../../../shared/product'
import { ProductTile } from '../../../shared/ProductTile'
import { DISAGREEMENT_LABELS } from '../model/evidenceLabels'
import type { HitVm, VerdictActions, VerdictOutcomeVm } from '../model/types'
import './scanVerdict.css'
import { ConfidenceBlock, ReasonNotes, VariantsSection } from './VariantsView'
import { judgeOf, judgeWords, MAX_VARIANTS, metaOf } from './variantsText'

/** Предупреждение у «Создать всё равно»: вердикт «не удалось определить» не доказывает, что товара нет. */
const CREATE_ANYWAY_WARNING = 'Создавайте, только если уверены: мы не смогли проверить, есть ли такой товар. Перед записью система ещё раз поищет похожий.'

/** Компактная карточка «Это он?»: миниатюра, название, процент совпадения и две кнопки в строку. */
function ItIsHeCard({ hit, photoUrl, actions }: { hit: HitVm; photoUrl: ProductPhotoUrl | undefined; actions: VerdictActions | undefined }) {
  return (
    <li className="platform-scan__card">
      <div className="platform-scan__card-main">
        <ProductThumb src={thumbOf(hit.commonPhotoUrl, hit.identities, photoUrl)} name={hit.name} size={48} />
        <div className="platform-scan__card-text">
          <p className="platform-scan__name">{hit.name}</p>
          <p className="platform-scan__meta">{metaOf(hit)}</p>
          <ConfidenceBlock hit={hit} verdict="found" />
        </div>
      </div>
      {actions !== undefined && (
        <div className="platform-scan__buttons">
          <Button variant="primary" icon={<CheckCircle2 size={18} aria-hidden="true" />} onClick={() => actions.confirmHit(hit)} actionId="platform.scan.confirm-hit">
            Да, это он
          </Button>
          <Button icon={<Eye size={18} aria-hidden="true" />} onClick={() => actions.openHit(hit)} actionId="platform.scan.open-hit">
            Только открыть
          </Button>
        </div>
      )}
    </li>
  )
}

/**
 * «Найдено: N» и список найденного — те же плашки, что и в обычной выдаче (#1166): фото слева, название до трёх строк, артикул и штрихкод,
 * процент в правом верхнем углу. Плашка открывает общий товар; у каждой, кроме предложенной в «Это он?», — кнопка «Это он».
 */
/** Строка плашки списка с итогом проверки по фото (RP-FB77 П4); проверки не было — строки нет. */
function judgeLines(hit: HitVm): { label: string; value: string }[] {
  const judge = judgeOf(hit)
  return judge === undefined ? [] : [{ label: judge.label, value: judgeWords(judge) }]
}

function FoundList({
  hits,
  offered,
  photoUrl,
  onOpenHit,
  actions,
}: {
  hits: readonly HitVm[]
  /** Товары, уже предложенные карточкой «Это он?»: второй кнопки подтверждения у их плашек нет. */
  offered: ReadonlySet<string>
  photoUrl: ProductPhotoUrl | undefined
  /** Открыть ОБЩУЮ карточку найденного товара. */
  onOpenHit: (hit: HitVm) => void
  actions: VerdictActions | undefined
}) {
  if (hits.length === 0) return null
  return (
    <>
      <p aria-live="polite">Найдено: {hits.length}</p>
      <ul className="platform-product-list" aria-label="Результаты поиска">
        {hits.map((hit) => (
          <ProductTile
            key={hit.productId}
            name={hit.name}
            refs={hit.identities}
            photoUrl={photoUrl}
            commonPhotoUrl={hit.commonPhotoUrl}
            onOpen={() => onOpenHit(hit)}
            lines={[
              { label: 'Артикул', value: hit.sku ?? '—' },
              { label: 'Штрихкод', value: hit.barcode ?? '—' },
              ...(hit.disagreement === null || hit.disagreement === undefined ? [] : [{ label: 'Внимание', value: DISAGREEMENT_LABELS[hit.disagreement] }]),
              ...judgeLines(hit),
            ]}
            matchPercent={hit.matchPercent}
            action={
              actions === undefined || offered.has(hit.productId) ? undefined : (
                <Button variant="ghost" icon={<CheckCircle2 size={18} aria-hidden="true" />} onClick={() => actions.confirmHit(hit)} actionId="platform.scan.confirm-hit">
                  Это он
                </Button>
              )
            }
          />
        ))}
      </ul>
    </>
  )
}

const NO_OFFER: ReadonlySet<string> = new Set()

/**
 * Слабые кандидаты (RP-FB62): лучшие из тех, что ниже нижнего порога списка. Выводятся ВСЕГДА открытым списком под вердиктом (решение владельца 24.10.2026):
 * вердикт остаётся баннером сверху, но человек должен хоть что-то найти и выбрать вручную, если система ошиблась. Честная подпись — «ниже порога —
 * вероятнее всего не он»; у каждой плашки процент и кнопка «Прикрепить фото к нему» (запоминает снимок за этим товаром, как «Это он»).
 */
function WeakList({
  hits,
  listMinPercent,
  photoUrl,
  onOpenHit,
  actions,
}: {
  hits: readonly HitVm[]
  listMinPercent: number | null
  photoUrl: ProductPhotoUrl | undefined
  /** Открыть ОБЩУЮ карточку найденного товара. */
  onOpenHit: (hit: HitVm) => void
  actions: VerdictActions | undefined
}) {
  if (hits.length === 0) return null
  const border = listMinPercent === null ? 'ниже порога списка' : `ниже порога ${listMinPercent}%`
  return (
    <section className="platform-scan__weak" aria-labelledby="platform-scan-weak-title" data-testid="weak-candidates">
      <h4 id="platform-scan-weak-title" className="platform-scan__weak-title">
        <ChevronDown size={18} aria-hidden="true" /> Ниже порога — вероятнее всего не он ({hits.length})
      </h4>
      <ul className="platform-product-list" aria-label="Кандидаты ниже порога">
        {hits.map((hit) => (
          <ProductTile
            key={hit.productId}
            name={hit.name}
            refs={hit.identities}
            photoUrl={photoUrl}
            commonPhotoUrl={hit.commonPhotoUrl}
            onOpen={() => onOpenHit(hit)}
            lines={[
              { label: 'Артикул', value: hit.sku ?? '—' },
              { label: 'Штрихкод', value: hit.barcode ?? '—' },
              { label: 'Не принято', value: border },
            ]}
            matchPercent={hit.matchPercent}
            action={
              actions === undefined ? undefined : (
                <Button variant="ghost" icon={<CheckCircle2 size={18} aria-hidden="true" />} onClick={() => actions.confirmHit(hit)} actionId="platform.scan.confirm-weak">
                  {actions.canWrite ? 'Прикрепить фото к нему' : 'Открыть его'}
                </Button>
              )
            }
          />
        ))}
      </ul>
    </section>
  )
}

/**
 * Ответ поиска по фото с вердиктом (RP-FB56) — один компонент и для «Выбрать фото», и для сканера. Порядок (договорённость с владельцем
 * 23.10.2026): над ним снимок с «Текстом на фото» и строка «Найдено за N с»; здесь — компактная подсказка вердикта, затем «Найдено: N» и
 * СПИСОК найденного. Список показывается всегда, независимо от вердикта. Пояснений «почему так решили» на экране нет.
 *
 * - «Это он?» (`found`): компактная карточка с процентом, «Да, это он» (запоминает снимок за этим товаром), «Только открыть» и «Нет, другой»
 *   (прячет подсказку; список остаётся).
 * - «Возможно, это» (`candidates`): заголовок и «Ничего из этого» (ведёт к «Нет в базе»); выбрать товар — «Это он» на его плашке.
 * - «Выберите вариант» (`candidates` + серия близнецов, RP-FB66): 2–4 варианта рядом с отличиями и пометкой про фото, «Это он» и «Прикрепить фото
 *   к нему» у каждого, «Что прочитано на этикетке» под ними.
 * - «Нет в базе» (`not-found`): значок и слово, «Создать новый товар» и «Найти товар и прикрепить фото», затем «Снять ещё раз».
 * - «Не удалось определить» (`undetermined`): «Это не значит, что товара нет»; главная — «Снять ещё раз», создание спрятано во вторую строку.
 *
 * Состояние различается значком и словом, не цветом. Без права менять общий каталог «Создать» и «Прикрепить» не показываются.
 */
export function ScanVerdictPanel({
  outcome,
  photoUrl,
  onOpenProduct,
  actions,
  retakeLabel,
  onRetake,
}: {
  outcome: VerdictOutcomeVm
  photoUrl: ProductPhotoUrl | undefined
  /** Открыть общую карточку товара плашки списка, когда действий вердикта нет (при них товар открывает `actions.openHit` и считает его найденным). */
  onOpenProduct: OpenCommonProduct
  actions: VerdictActions | undefined
  /** «Снять ещё раз» в сканере и «Выбрать другое фото» на экране поиска. */
  retakeLabel: string
  /** Снять или выбрать заново; не задано — кнопки нет. */
  onRetake: (() => void) | undefined
}) {
  // «Нет, другой» прячет подсказку «Это он?»; «Ничего из этого» переводит подсказку в «Нет в базе». Список найденного остаётся в обоих случаях.
  // Отказ помнится ПО ТОВАРАМ, которые человек отклонил: ранний список (по тексту, пока считается фото) заменяется следующим, и отказ от тех же товаров остаётся в силе,
  // а если итог предлагает другой товар, это новое предложение и подсказка показывается снова. Новый поиск размонтирует панель, и выбор не переходит на новый ответ.
  const [rejectedIds, setRejectedIds] = useState<ReadonlySet<string>>(NO_OFFER)
  const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(NO_OFFER)
  const rejected = outcome.confident.length > 0 && outcome.confident.every((hit) => rejectedIds.has(hit.productId))
  const shownIds = [...outcome.confident, ...outcome.possible].map((hit) => hit.productId)
  const dismissed = dismissedIds.size > 0 && shownIds.length > 0 && shownIds.every((id) => dismissedIds.has(id))
  const canWrite = actions?.canWrite === true
  const openHit = (hit: HitVm) => (actions === undefined ? onOpenProduct(hit.productId) : actions.openHit(hit))
  const retake = () => {
    actions?.skipped()
    onRetake?.()
  }
  // Отказ человека — метка для обучения поиска (RP-FB77): «Нет, другой» — предложенные в «Это он?» не те, «Ничего из этого» — среди показанного нет.
  const reject = () => {
    setRejectedIds(new Set(outcome.confident.map((hit) => hit.productId)))
    actions?.rejectOffered(outcome.confident)
  }
  const noneOfThese = () => {
    setDismissedIds(new Set(shownIds))
    actions?.noneOfThese()
  }
  const retakeButton = (variant: 'primary' | 'secondary' | 'ghost') =>
    onRetake === undefined ? null : (
      <Button variant={variant} icon={<RotateCcw size={18} aria-hidden="true" />} onClick={retake} actionId="platform.scan.retake">
        {retakeLabel}
      </Button>
    )

  const afterReject: VerdictOutcomeVm['verdict'] = outcome.possible.length > 0 ? 'candidates' : 'not-found'
  const shown = dismissed ? 'not-found' : outcome.verdict === 'found' && rejected ? afterReject : outcome.verdict
  // «Выберите вариант» (RP-FB66): серия близнецов от сервера, 2–4 карточки рядом с отличиями. Остальные позиции — списком ниже, как всегда.
  const variants = shown === 'candidates' && outcome.twin !== undefined && outcome.twin.length >= 2 ? outcome.twin.slice(0, MAX_VARIANTS) : null
  const inCards: ReadonlySet<string> = variants === null ? NO_OFFER : new Set(variants.map((hit) => hit.productId))
  const everything = [...outcome.confident, ...outcome.possible].filter((hit) => !inCards.has(hit.productId))
  const offered = shown === 'found' ? new Set(outcome.confident.map((hit) => hit.productId)) : NO_OFFER
  // Список под вердиктом показывается ВСЕГДА: найденное, а за ним — слабые кандидаты ниже порога с честной подписью (RP-FB62).
  const list = (
    <>
      <FoundList hits={everything} offered={offered} photoUrl={photoUrl} onOpenHit={openHit} actions={actions} />
      <WeakList hits={(outcome.weak ?? []).filter((hit) => !inCards.has(hit.productId))} listMinPercent={outcome.listMinPercent ?? null} photoUrl={photoUrl} onOpenHit={openHit} actions={actions} />
    </>
  )

  if (shown === 'found') {
    return (
      <>
        <section className="platform-scan" aria-labelledby="platform-scan-title" data-verdict="found">
          <h3 id="platform-scan-title" className="platform-scan__title">
            <CheckCircle2 size={20} aria-hidden="true" /> Это он?
          </h3>
          <ul className="platform-scan__cards" aria-label="Это он?">
            {outcome.confident.map((hit) => (
              <ItIsHeCard key={hit.productId} hit={hit} photoUrl={photoUrl} actions={actions} />
            ))}
          </ul>
          <div className="platform-scan__footer">
            <Button variant="ghost" icon={<XCircle size={18} aria-hidden="true" />} onClick={reject} actionId="platform.scan.not-this">
              Нет, другой
            </Button>
          </div>
        </section>
        {list}
      </>
    )
  }

  if (variants !== null) {
    return (
      <>
        <VariantsSection
          variants={variants}
          verdict="candidates"
          reasons={outcome.reasons}
          recognized={outcome.recognized}
          photoUrl={photoUrl}
          actions={actions}
          onNoneOfThese={noneOfThese}
        />
        {list}
      </>
    )
  }

  if (shown === 'candidates') {
    return (
      <>
        <section className="platform-scan" aria-labelledby="platform-scan-title" data-verdict="candidates">
          <h3 id="platform-scan-title" className="platform-scan__title">
            <Search size={20} aria-hidden="true" /> Возможно, это
          </h3>
          <ReasonNotes reasons={outcome.reasons} />
          <div className="platform-scan__footer">
            <Button variant="ghost" icon={<XCircle size={18} aria-hidden="true" />} onClick={noneOfThese} actionId="platform.scan.none-of-these">
              Ничего из этого
            </Button>
          </div>
        </section>
        {list}
      </>
    )
  }

  if (shown === 'not-found') {
    return (
      <>
        <section className="platform-scan" aria-labelledby="platform-scan-title" data-verdict="not-found">
          <h3 id="platform-scan-title" className="platform-scan__title">
            <CircleHelp size={20} aria-hidden="true" /> Подходящего не нашлось — такого товара в базе нет
          </h3>
          {(dismissed || rejected) && <p className="platform-scan__muted">Вы отклонили предложенные варианты.</p>}
          {actions !== undefined && canWrite ? (
            <div className="platform-scan__big-buttons">
              <Button variant="primary" icon={<Plus size={18} aria-hidden="true" />} onClick={actions.createNew} actionId="platform.scan.create-new">
                Создать новый товар
              </Button>
              <Button icon={<Images size={18} aria-hidden="true" />} onClick={actions.findAndAttach} actionId="platform.scan.find-and-attach">
                Найти товар и прикрепить фото
              </Button>
            </div>
          ) : (
            actions !== undefined && (
              <p className="platform-scan__muted">Создавать товары и прикреплять фото может тот, кому выдано право менять общий каталог.</p>
            )
          )}
          <div className="platform-scan__footer">{retakeButton('secondary')}</div>
        </section>
        {list}
      </>
    )
  }

  return (
    <>
      <section className="platform-scan" aria-labelledby="platform-scan-title" data-verdict="undetermined">
        <h3 id="platform-scan-title" className="platform-scan__title">
          <AlertTriangle size={20} aria-hidden="true" /> Не удалось определить
        </h3>
        <p className="platform-scan__muted">Это не значит, что товара нет. Снимите ближе, ровнее и без бликов — или выберите другое фото.</p>
        <div className="platform-scan__big-buttons">{retakeButton('primary')}</div>
        {actions !== undefined && canWrite && (
          <div className="platform-scan__second">
            <p className="platform-scan__muted">{CREATE_ANYWAY_WARNING}</p>
            <div className="platform-scan__second-buttons">
              <Button variant="ghost" onClick={actions.createNew} actionId="platform.scan.create-anyway">
                Создать всё равно
              </Button>
              <Button variant="ghost" onClick={actions.findAndAttach} actionId="platform.scan.find-and-attach">
                Найти товар и прикрепить фото
              </Button>
            </div>
          </div>
        )}
      </section>
      {list}
    </>
  )
}
